import clsx from "clsx";
import { FC, Ref, useEffect, useRef } from "react";

import { FullSectionType } from "components/server-types";
import { t } from "i18n/i18n";
import { LegacyButton } from "mds/components/LegacyButton";
import {
  ClickEvent,
  Menu,
  MenuDirection,
  MenuInstance,
  MenuItem,
  MenuType,
  SubMenu,
} from "mds/components/Menu";
import { ChevronDownIcon } from "mds/icons";
import { storeApi } from "store/index";
import { selectFullSectionsSorted } from "store/selectors";
import { useListSelector } from "store/store-hooks";
import { useIsArchived } from "utils/hooks/use-is-archived";

export type SectionMenuProps = {
  selectedSectionId: string | null;
  onSectionChange: (sectionId: string) => void;
  menuClassName?: string;
  menuType?: MenuType;
  size?: "xs" | "s";
  showEmptyOption?: boolean;
  /** Text for the empty option when showEmptyOption is true. Defaults to "Select section". */
  emptyOptionText?: string;
  direction?: MenuDirection;
  /** Filter sections. Pass a predicate function. */
  filterBy?: (section: FullSectionType) => boolean;
};

export const SectionMenu: FC<SectionMenuProps> = ({
  selectedSectionId,
  onSectionChange,
  menuClassName,
  menuType = "submenu",
  size = "xs",
  showEmptyOption = true,
  emptyOptionText = t("import_modal.select_section"),
  direction = "bottom",
  filterBy,
}) => {
  const subMenuRef: Ref<MenuInstance> = useRef(null);
  const sections = useListSelector(selectFullSectionsSorted);
  const { isActive } = useIsArchived();
  let filteredSections = sections.filter(isActive);
  if (filterBy != null) {
    filteredSections = filteredSections.filter(filterBy);
  }
  type SectionOption = Pick<FullSectionType, "id" | "full_title">;
  const emptyOption: SectionOption = { id: "", full_title: emptyOptionText };
  const sectionOptions: SectionOption[] = showEmptyOption
    ? [emptyOption, ...filteredSections]
    : filteredSections;

  const selectedSection = sectionOptions.find((s) => s.id === (selectedSectionId ?? ""));

  useEffect(() => {
    storeApi.sections.list();
  }, []);

  const onItemClick = (e: ClickEvent) => {
    const section = e.value as Pick<FullSectionType, "id" | "full_title">;
    onSectionChange(section.id);
    // eslint-disable-next-line no-param-reassign
    e.keepOpen = true;
    subMenuRef.current?.closeMenu();
  };

  const menuButton = (
    <LegacyButton className="w-full gap-1" kind="secondary" size={size}>
      {selectedSection?.full_title ?? ""}
      <ChevronDownIcon />
    </LegacyButton>
  );

  const DropdownMenu = sectionOptions.map((sectionOption) => {
    const selected = (selectedSectionId ?? "") === sectionOption.id;
    return (
      <MenuItem
        checked={selected}
        className={clsx({ selected, "text-white": selected }, "center dropdown-item")}
        key={sectionOption.id}
        value={sectionOption}
        onClick={onItemClick}
      >
        {sectionOption.full_title}
      </MenuItem>
    );
  });

  const menuProps = {
    className: "dropdown",
    instanceRef: subMenuRef,
    children: DropdownMenu,
    size,
    direction,
  };

  return menuType === "menu" ? (
    <Menu
      {...menuProps}
      direction={direction}
      menuButton={menuButton}
      menuClassName={menuClassName}
      position="initial"
    />
  ) : (
    <SubMenu
      {...menuProps}
      direction={direction}
      label={menuButton}
      openTrigger="clickOnly"
      position="initial"
    />
  );
};
